import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Lock, Check, X, Loader2, Eye, EyeOff } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";
import { useToast } from "@/hooks/use-toast";

const MIN_LENGTH = 8;

const schema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string()
    .min(MIN_LENGTH, `Must be at least ${MIN_LENGTH} characters`)
    .regex(/[A-Z]/, "Must contain at least one uppercase letter")
    .regex(/[0-9]/, "Must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Must contain at least one special character"),
  confirmPassword: z.string().min(1, "Please confirm your new password"),
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof schema>;

function PolicyRow({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-white/30 shrink-0" />
      )}
      <span className={`text-xs ${met ? "text-green-400" : "text-white/40"}`}>{label}</span>
    </div>
  );
}

export function ChangePasswordCard() {
  const { toast } = useToast();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
    mode: "onChange",
  });

  const newPassword = useWatch({ control: form.control, name: "newPassword" });
  const confirmPassword = useWatch({ control: form.control, name: "confirmPassword" });

  const meetsLength = newPassword.length >= MIN_LENGTH;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function onSubmit(values: FormValues) {
    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword: values.currentPassword, newPassword: values.newPassword }),
      });
      toast({ title: "Password updated", description: "You've been signed out of all other devices." });
      form.reset();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update password";
      if (msg.toLowerCase().includes("incorrect")) {
        form.setError("currentPassword", { message: "Current password is incorrect" });
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    }
  }

  return (
    <Card className="glass-panel border-white/5">
      <CardHeader>
        <CardTitle className="text-xl font-display text-white flex items-center gap-2">
          <Lock className="w-5 h-5" /> Change Password
        </CardTitle>
        <p className="text-sm text-muted-foreground">Update your login password.</p>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">

            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80">Current password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showCurrent ? "text" : "password"}
                        placeholder="Enter current password"
                        autoComplete="current-password"
                        className="bg-background border-white/10 pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80">New password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showNew ? "text" : "password"}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        className="bg-background border-white/10 pr-10"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  {newPassword.length > 0 && (
                    <div className="mt-2 p-3 rounded-lg bg-white/5 border border-white/8 space-y-2">
                      <PasswordStrengthMeter password={newPassword} />
                      <div className="space-y-1.5">
                        <PolicyRow met={meetsLength}  label={`At least ${MIN_LENGTH} characters`} />
                        <PolicyRow met={hasUppercase} label="At least one uppercase letter" />
                        <PolicyRow met={hasNumber}    label="At least one number" />
                        <PolicyRow met={hasSpecial}   label="At least one special character (!@#$…)" />
                      </div>
                    </div>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/80">Confirm new password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirm ? "text" : "password"}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        className={`bg-background border-white/10 pr-10 transition-colors ${
                          passwordsMatch
                            ? "border-green-500/50 focus-visible:border-green-500 focus-visible:ring-green-500"
                            : passwordsMismatch
                            ? "border-red-500/50 focus-visible:border-red-500 focus-visible:ring-red-500"
                            : ""
                        }`}
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                        tabIndex={-1}
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </FormControl>
                  {confirmPassword.length > 0 && (
                    <div className={`flex items-center gap-1.5 text-xs mt-1 ${passwordsMatch ? "text-green-400" : "text-red-400"}`}>
                      {passwordsMatch ? (
                        <><Check className="w-3.5 h-3.5" /> Passwords match</>
                      ) : (
                        <><X className="w-3.5 h-3.5" /> Passwords do not match</>
                      )}
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/80"
            >
              {form.formState.isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> Update Password</>
              )}
            </Button>

          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
